// src/Pages/Projects.jsx
import React, { useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useReducedMotion,
  AnimatePresence
} from "framer-motion";
import { projects, workProjects } from "../data/projectsData";

// ─────────────────────────────────────────────
// Animations
// ─────────────────────────────────────────────
const fadeUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const fadeUpDelayed = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.55, delay } },
});

// ─────────────────────────────────────────────
// Compact Work / Client project card (Bento Grid)
// ─────────────────────────────────────────────
function CompactWorkProject({ title, year, role, live, repo, description, stack = [] }) {
  return (
    <motion.article
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      viewport={{ once: true, amount: 0.1 }}
      className="flex flex-col h-full p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors duration-300 group"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        {role && (
          <span className="text-[10px] font-mono tracking-widest text-pink-400 uppercase">
            {role}
          </span>
        )}
        <span className="text-[10px] text-neutral-500 font-mono">{year}</span>
      </div>
      
      <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-pink-300 transition-colors">
        {title}
      </h3>
      
      <p className="text-sm text-neutral-400 leading-relaxed mb-6 flex-grow">
        {description}
      </p>

      {stack.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-auto pt-4 border-t border-white/5">
          {stack.map(tech => (
            <span key={tech} className="text-[10px] px-2 py-1 rounded-md bg-white/5 text-neutral-300 border border-white/10">
              {tech}
            </span>
          ))}
        </div>
      )}
      
      {(live || repo) && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/5">
          {live && (
            <a href={live} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1.5 rounded-full bg-pink-500/10 text-pink-300 hover:bg-pink-500/20 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500">
              Live ↗
            </a>
          )}
          {repo && (
            <a href={repo} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-1.5 rounded-full bg-white/5 text-white hover:bg-white/10 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
              Code ⌁
            </a>
          )}
        </div>
      )}
    </motion.article>
  );
}


// ─────────────────────────────────────────────
// Personal project row — your original FeaturedProject, unchanged
// ─────────────────────────────────────────────
function FeaturedProject({
  title,
  year,
  role,
  img,
  live,
  repo,
  description,
  stack = [],
  flip = false,
}) {
  const rowRef = useRef(null);
  const prefersReduced = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: rowRef,
    offset: ["start 0.85", "end 0.15"],
  });

  const imgYRaw  = useTransform(scrollYProgress, [0, 0.5, 1], [30, -10, -30]);
  const metaYRaw = useTransform(scrollYProgress, [0, 0.5, 1], [20, -6, -20]);
  const scaleRaw = useTransform(scrollYProgress, [0, 0.5, 1], [0.988, 1.015, 0.988]);

  const imgY  = useSpring(prefersReduced ? 0 : imgYRaw,  { stiffness: 180, damping: 18, mass: 0.35 });
  const metaY = useSpring(prefersReduced ? 0 : metaYRaw, { stiffness: 190, damping: 20, mass: 0.35 });
  const scale = useSpring(prefersReduced ? 1 : scaleRaw, { stiffness: 160, damping: 22, mass: 0.4  });

  return (
    <motion.article
      ref={rowRef}
      initial="initial"
      whileInView="animate"
      viewport={{ once: false, amount: 0.3 }}
      variants={fadeUp}
      style={{ scale }}
      className={`
        grid items-center gap-8 md:gap-12 transform-gpu
        ${flip ? "md:grid-cols-[1fr_1.35fr] md:[&>div:nth-child(1)]:order-2 md:[&>div:nth-child(2)]:order-1" : "md:grid-cols-[1.35fr_1fr]"}
      `}
    >
      {/* Visual */}
      <motion.div
        style={{ y: imgY }}
        className="group relative overflow-hidden rounded-xl bg-white/5 ring-1 ring-white/10 aspect-video"
      >
        <a
          href={live}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${title} – open live`}
          className="focus:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-lg block w-full h-full"
        >
          <img
            src={img}
            alt={title}
            className="w-full h-full object-cover object-top transition-transform duration-200 group-hover:scale-[1.04]"
            loading="lazy"
          />
        </a>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      </motion.div>

      {/* Meta */}
      <motion.div
        style={{ y: metaY }}
        className="space-y-4 transform-gpu py-2 md:py-6"
      >
        <div className="flex flex-wrap items-center gap-3 text-sm text-[#A1A1A1]">
          <span className="border border-[#A1A1A1] rounded-full px-3 py-1">{year}</span>
          {stack.length > 0 && (
            <span className="border border-[#A1A1A1] rounded-full px-3 py-1">
              {stack.join(" • ")}
            </span>
          )}
        </div>

        <h3 className="text-2xl md:text-3xl font-semibold">{title}</h3>
        {role        && <p className="text-base text-neutral-300">{role}</p>}
        {description && <p className="text-base text-neutral-300 max-w-prose">{description}</p>}

        <div className="flex flex-wrap gap-3 pt-1">
          {live && (
            <a
              href={live}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm hover:bg-white/10 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              Live <span aria-hidden>↗</span>
            </a>
          )}
          {repo && (
            <a
              href={repo}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm hover:bg-white/10 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              Code <span aria-hidden>⌁</span>
            </a>
          )}
        </div>
      </motion.div>
    </motion.article>
  );
}


// ─────────────────────────────────────────────
// Main export — named, same as your original
// ─────────────────────────────────────────────
export function Projects() {
  const [showAllClient, setShowAllClient] = useState(false);
  const visibleClientProjects = showAllClient ? workProjects : workProjects.slice(0, 6);

  return (
    <section
      data-theme="dark"
      className="full-bleed bg-[#171717] text-white scroll-mt-24 pt-24 pb-64"
      style={{
        "--padY": "clamp(6rem, 6vw, 8rem)",
        "--maxW": "1100px",
        "--rowGap": "clamp(8rem, 4vw, 8rem)",
      }}
    >
      <div id="Projects" />

      <div className="container py-[var(--padY)]" style={{ maxWidth: "var(--maxW)" }}>

        {/* ── Heading ── */}
        <motion.h1
          id="Projects1"
          className="font-sat font-thin text-5xl md:text-7xl lg:text-8xl text-center mb-12 md:mb-16 pt-8 pb-14"
          initial="initial"
          whileInView="animate"
          viewport={{ once: false, amount: 0.3 }}
          variants={fadeUp}
        >
          PROJECTS
        </motion.h1>

        {/* ══════════════════════════════════════
            PERSONAL — shown first (Visual Highlights)
        ══════════════════════════════════════ */}
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: false, amount: 0.3 }}
          variants={fadeUpDelayed(0)}
          className="flex items-center gap-4 mb-4"
        >
          <span className="w-2 h-2 rounded-full bg-white shrink-0" />
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-neutral-300">
            Featured Work
          </span>
          <div className="flex-1 h-px bg-white/[0.08]" />
          <span className="font-mono text-[10px] text-neutral-400">
            {projects.length} projects
          </span>
        </motion.div>

        <motion.p
          initial="initial"
          whileInView="animate"
          viewport={{ once: false, amount: 0.5 }}
          variants={fadeUpDelayed(0.08)}
          className="text-neutral-300 text-sm max-w-lg mb-14 leading-relaxed"
        >
          A selection of full-stack and frontend products I've built.
        </motion.p>

        <div className="grid gap-[var(--rowGap)] mb-[var(--rowGap)]">
          {projects.map((p) => (
            <FeaturedProject key={p.title} {...p} />
          ))}
        </div>

        {/* ══════════════════════════════════════
            WORK / CLIENT — shown second (Bento Grid)
        ══════════════════════════════════════ */}
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: false, amount: 0.3 }}
          variants={fadeUpDelayed(0.05)}
          className="flex items-center gap-4 mt-16 mb-4"
        >
          <span className="w-2 h-2 rounded-full bg-pink-500 shrink-0" />
          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-pink-400">
            Internal Tools & Client Projects
          </span>
          <div className="flex-1 h-px bg-white/[0.08]" />
          <span className="font-mono text-[10px] text-neutral-400">
            {workProjects.length} projects
          </span>
        </motion.div>

        <motion.p
          initial="initial"
          whileInView="animate"
          viewport={{ once: false, amount: 0.5 }}
          variants={fadeUpDelayed(0.08)}
          className="text-neutral-300 text-sm max-w-lg mb-12 leading-relaxed"
        >
          Automation tools and internal dashboards built for clients using n8n and custom
          frontends. Designed to eliminate manual work and scale operations.
        </motion.p>

        <motion.div layout className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-12">
          <AnimatePresence mode="popLayout">
            {visibleClientProjects.map((p) => (
              <CompactWorkProject key={p.title} {...p} />
            ))}
          </AnimatePresence>
        </motion.div>

        {workProjects.length > 6 && (
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex justify-center"
          >
            <button
              onClick={() => setShowAllClient(!showAllClient)}
              className="inline-flex items-center gap-2 rounded-full border border-pink-500/30 bg-pink-500/10 px-6 py-3 text-sm font-medium text-pink-300 hover:bg-pink-500/20 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500"
            >
              {showAllClient ? "Show Less ↑" : `View ${workProjects.length - 6} More Internal Tools ↓`}
            </button>
          </motion.div>
        )}

      </div>
    </section>
  );
}